import React, { useEffect, useRef } from 'react';

const About = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    canvas.width = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;

    const nodes = Array.from({ length: 28 }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      vx: (Math.random() - 0.5) * 0.4,
      vy: (Math.random() - 0.5) * 0.4,
      r: Math.random() * 2.5 + 1,
    }));

    let animId;
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      nodes.forEach(n => {
        n.x += n.vx; n.y += n.vy;
        if (n.x < 0 || n.x > canvas.width) n.vx *= -1;
        if (n.y < 0 || n.y > canvas.height) n.vy *= -1;
      });
      nodes.forEach((a, i) => {
        nodes.slice(i + 1).forEach(b => {
          const d = Math.hypot(a.x - b.x, a.y - b.y);
          if (d < 120) {
            ctx.beginPath();
            ctx.strokeStyle = `rgba(139,92,246,${0.7 * (1 - d / 120)})`;
            ctx.lineWidth = 0.7;
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.stroke();
          }
        });
        ctx.beginPath();
        ctx.arc(a.x, a.y, a.r, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(139,92,246,0.7)';
        ctx.fill();
      });
      animId = requestAnimationFrame(draw);
    };
    draw();
    return () => cancelAnimationFrame(animId);
  }, []);

  return (
    <div style={{ fontFamily: "'Georgia', 'Times New Roman', serif", background: '#faf9f7', minHeight: '100vh', paddingTop: '4rem' }}>

      {/* Hero */}
      <div style={{ position: 'relative', overflow: 'hidden', background: '#0e0b14', padding: '5rem 1.5rem 4rem' }}>
        <canvas ref={canvasRef} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0.8 }} />
        <div style={{ position: 'relative', maxWidth: '720px', margin: '0 auto', textAlign: 'center' }}>
          <h1 style={{
            fontSize: 'clamp(2rem, 5vw, 3.5rem)',
            fontWeight: '400',
            lineHeight: 1.15,
            color: '#f5f0ff',
            marginBottom: '1.5rem',
            letterSpacing: '-0.01em'
          }}>
            Bem-vindo à<br />
            <em style={{ fontStyle: 'italic', color: '#c4b5fd' }}>Plexo Natural Network</em>
          </h1>
          <p style={{ color: '#9ca3af', fontSize: '1.1rem', lineHeight: 1.8, maxWidth: '520px', margin: '0 auto' }}>
            Você é um novo ponto nessa rede — vivo, conectado, verificável.
          </p>
        </div>
      </div>

      {/* Corpo principal */}
      <div style={{ maxWidth: '760px', margin: '0 auto', padding: '4rem 1.5rem' }}>

        {/* Intro */}
        <Section>
          <p style={bodyText}>
            A Plexo Natural nasceu no Brasil — território reconhecido pela afetividade, pelo encontro e pela humanidade em carne e osso.
          </p>
          <p style={bodyText}>
            Nosso foco é simples e profundo: <strong style={{ color: '#6d28d9' }}>uma vida mais natural em tempos digitais.</strong>
          </p>
          <p style={bodyText}>
            Plantas, animais e ecossistemas nunca deixaram de evoluir. A natureza não é estática — ela se adapta, responde, se reinventa. E, da mesma forma, evolui conosco. Essa integração entre natural e digital não é ruptura. É continuidade.
          </p>
          <p style={bodyText}>
            Ainda assim, acreditamos que o natural deve ser preservado em sua integridade: em seu estado bruto, original, vivo.
          </p>
        </Section>

        <Divider />

        {/* Portal */}
        <Section>
          <SectionTitle>O Portal Network</SectionTitle>
          <p style={bodyText}>
            A Network Plexo Natural é um espaço para registro, expressão, validação e difusão de existências, presenças e manifestações humanas, biológicas e digitais — dentro de uma lógica descentralizada emergente.
          </p>
          <p style={bodyText}>Reunimos aqui nossos <strong style={{ color: '#6d28d9' }}>Serviços Originais Plexo Natural:</strong></p>
        </Section>

        {/* Cards de serviços */}
        <div style={{ display: 'grid', gap: '1.25rem', margin: '2rem 0' }}>
          <ServiceCard
            color="#7c3aed"
            bg="#f5f3ff"
            title="🔹 ProofOfLife"
            text="Registro público de prova de vida, movimento e som. Uma manifestação viva da presença humana no tempo. Cada registro gera uma hash criptografada armazenada na blockchain Polygon — garantindo autenticidade, permanência e inviolabilidade."
          />
          <ServiceCard
            color="#16a34a"
            bg="#f0fdf4"
            title="🔹 ProofOfExistence"
            text="Registro de prova de existência de agentes biológicos, metafísicos e digitais. O testemunho é feito em texto, com validação criptográfica registrada na blockchain Polygon. Existir é deixar rastro. Aqui, ele se torna verificável."
          />
          <ServiceCard
            color="#7c3aed"
            bg="#f5f3ff"
            title="🔹 Decoder"
            text="Ferramenta de decodificação e exibição das provas registradas. Permite leitura pública, validação e transparência dos dados existenciais gravados no sistema."
          />
          <ServiceCard
            color="#16a34a"
            bg="#f0fdf4"
            title="🔹 Live Art Performance Test"
            text="Teste heurístico voltado a performances ao vivo e livestreams. O modelo analisa movimento, postura, ritmo de ação e temperatura performática — gerando uma pontuação geral baseada em métricas treinadas pelo sistema. Atingir acima de 30 é raro. Mas toda expressão carrega valor."
            highlight="Coragem de se expor também é métrica de vida."
          />
        </div>

        <Divider />

        {/* Perfis */}
        <Section>
          <SectionTitle>Perfis e Expressão</SectionTitle>
          <p style={bodyText}>
            Os perfis na Network podem exibir provas registradas, testes performáticos, expressões artísticas e ações ao vivo. Porque nada é mais natural ao ser humano do que se expressar.
          </p>
          <p style={bodyText}>
            A arte, a cultura e o gesto são sintomas saudáveis de um organismo social vivo.
          </p>
          <Callout text="Se a sociedade é corpo, a cultura é seu sistema nervoso." />
        </Section>

        <Divider />

        {/* Eventos Internacionais */}
        <Section>
          <SectionTitle>Eventos Internacionais</SectionTitle>
          <p style={bodyText}>
            A <strong>Mostra Internacional de Performance Arte em São Paulo</strong> — <em>SerformanceP</em> — e o <strong>Encontro Internacional de Arte de Rua</strong> — <em>Manifesp</em> — são iniciativas criadas e organizadas pelo fundador da Plexo Natural.
          </p>
          <p style={bodyText}>
            Se você é artista e tem algo a apresentar, uma peça a comercializar, ou se interessa por Performance Art e Street Art, siga-nos:
          </p>
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', margin: '1rem 0' }}>
            <SocialLink href="https://instagram.com/serformancep" label="@serformancep" sublabel="Performance Art" />
            <SocialLink href="https://instagram.com/manifesp" label="@manifesp" sublabel="Arte de Rua / Street Art" />
          </div>
          <p style={{ ...bodyText, marginTop: '1.5rem' }}>
            Mencione se você estudou com o fundador ou participou de um de nossos cursos.
          </p>
          <p style={bodyText}>
            Na <em>Manifesp</em>, desenvolvemos pesquisa e desenvolvimento em Web3: empregamos o token RUA na Polygon e tokenizamos obras de artistas exibidas em galeria digital de NFTs. Na <em>SerformanceP</em>, criamos um jogo de cards estilo Super Trunfo, tokenizamos cards de apresentação de artistas e realizamos uma das primeiras performances de arte com IA generativa — ainda em 2022.
          </p>
          <p style={{ marginBottom: '0.5rem' }}>
            <a href="https://opensea.io/item/polygon/0x2953399124f0cbb46d2cbacd8a89cf0599974963/17034803097508768033945103114645776806787710703427463433629346565348950802433"
              target="_blank" rel="noopener noreferrer"
              style={{ color: '#7c3aed', fontSize: '0.85rem', wordBreak: 'break-all' }}>
              Ver na OpenSea →
            </a>
          </p>
          <Callout text="Descentralizar é replicar o comportamento dos biomas: múltiplos centros, interdependência, resiliência." />
          <div style={{ background: '#faf5ff', borderLeft: '3px solid #7c3aed', padding: '1.25rem 1.5rem', borderRadius: '0 8px 8px 0', marginTop: '1.5rem' }}>
            <p style={{ margin: 0, color: '#4b5563', fontStyle: 'italic', lineHeight: 1.8 }}>
              <strong style={{ color: '#6d28d9', fontStyle: 'normal' }}>Mr. Plexo</strong>, em sua calma botânica habitual, costuma dizer:<br />
              <span style={{ fontSize: '1.05rem' }}>"Registrar a vida é plantar memória no tempo." 🌱⛓️</span>
            </p>
          </div>
        </Section>

        <Divider />

        {/* PleNaScreen */}
        <Section>
          <SectionTitle>PlenaScreen</SectionTitle>
          <p style={bodyText}>
            A PleNaScreen é nossa televisão aberta de múltiplas fontes. Um ambiente onde o telespectador escolhe o que assistir, quando assistir e de onde assistir — sem curadoria centralizada, sem filtros ideológicos impostos.    
          </p>
          <p style={bodyText}>
            Reunimos canais ao vivo de TV aberta e digital, transmissões globais, conteúdos sob demanda de domínio público, arquivos históricos da internet e vídeos recentes de redes descentralizadas ou das redes sociais conhecidas. A inteligência artificial auxilia na busca, organização e cruzamento de conteúdos — e você pode trazer suas próprias ias e agentes, tirando seus documentos, ou apis, que são passaportes das ias para acessar outros sistemas.
            Aqui com seus agentes ou os nossos nativos, todo esse inúmero conteúdo fica acessível e viável para pesquisas. Literalmente sua companhia para TV.
          </p>
          <p style={bodyText}>
            Um hub de entretenimento, inteligência, pesquisa e autonomia de atenção.
          </p>
          <div style={{ textAlign: 'center', margin: '1.5rem 0' }}>
            <a href="https://plenascreen.com" target="_blank" rel="noopener noreferrer"
              style={{ display: 'inline-block', padding: '0.75rem 2rem', background: 'linear-gradient(135deg, #7c3aed, #16a34a)', color: '#fff', borderRadius: '6px', fontFamily: 'monospace', fontWeight: '700', letterSpacing: '0.05em', textDecoration: 'none', fontSize: '1rem' }}>
              plenascreen.com →
            </a>
          </div>
          <p style={{ ...bodyText, fontSize: '0.9rem', color: '#6b7280' }}>
            A versão gratuita é mantida com anúncios discretos que sustentam a infraestrutura, os agentes de IA e a operação técnica. A versão premium remove os anúncios.
          </p>
        </Section>

        <Divider />

        {/* E-commerce */}
        <Section>
          <SectionTitle>Produtos Naturais & Ecológicos</SectionTitle>
          <p style={bodyText}>
            A rede também se manifesta no consumo consciente. Reunimos fornecedores parceiros e produtores da rede - que pode ser o seu caso - em sintonia com a conservação do meio ambiente — produtos naturais, artísticos e ecológicos disponíveis em nossas plataformas de comércio.
          </p>
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', margin: '1.5rem 0' }}>
            <ShopLink href="https://plexonatural.com" label="plexonatural.com" sublabel="Loja oficial Plexo Natural" icon="🌿" />
            <ShopLink href="https://shopee.com/lojaapp" label="shopee.com/lojaapp" sublabel="Loja parceira na Shopee" icon="🛒" />
          </div>
          <p style={{ ...bodyText, fontSize: '0.9rem', color: '#6b7280' }}>
            Consumir com intenção também é um ato de presença no mundo.
          </p>
        </Section>

        <Divider />

        {/* Próximo passo */}
        <Section>
          <SectionTitle>Seu Próximo Passo</SectionTitle>
          <p style={{ ...bodyText, textAlign: 'center' }}>Se você chegou até aqui, já faz parte.</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '1rem', margin: '2rem 0' }}>
            {['Registrar sua ProofOfLife', 'Criar seu perfil público', 'Testar sua performance ao vivo', 'Explorar a PleNaTV'].map(s => (
              <div key={s} style={{ background: '#fff', border: '1px solid #e9d5ff', borderRadius: '10px', padding: '1.25rem 1rem', textAlign: 'center', fontSize: '0.9rem', color: '#4b5563', lineHeight: 1.5 }}>
                {s}
              </div>
            ))}
          </div>
          <p style={{ ...bodyText, textAlign: 'center', fontWeight: '600', color: '#6d28d9' }}>
            A rede não é para espectadores passivos. É para presenças ativas.
          </p>
        </Section>

        <Divider />

        {/* Missão */}
        <Section>
          <SectionTitle>Nossa Missão</SectionTitle>
          <p style={bodyText}>
            Promover, registrar e divulgar expressões pessoais e coletivas que fortaleçam a vida natural, a cultura orgânica, a descentralização consciente e a presença humana no digital.
          </p>
        </Section>

        {/* Assinatura */}
        <div style={{ textAlign: 'center', padding: '3rem 0 1rem', borderTop: '1px solid #e9d5ff', marginTop: '2rem' }}>
          <p style={{ fontSize: '1.8rem', fontWeight: '400', fontStyle: 'italic', background: 'linear-gradient(90deg, #7c3aed, #16a34a)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', marginBottom: '0.5rem' }}>
            Plexo Natural
          </p>
          <p style={{ color: '#9ca3af', fontSize: '1rem', letterSpacing: '0.08em' }}>Vida Original, Hoje 🌿</p>
        </div>

        {/* Voltar */}
        <div style={{ textAlign: 'center', paddingTop: '2rem' }}>
          <a href="/" style={{ display: 'inline-block', padding: '1rem 2.5rem', background: 'linear-gradient(135deg, #7c3aed, #16a34a)', color: '#fff', borderRadius: '8px', fontWeight: '700', fontFamily: 'sans-serif', fontSize: '0.95rem', textDecoration: 'none', letterSpacing: '0.02em', boxShadow: '0 4px 20px rgba(124,58,237,0.25)' }}>
            ← Voltar e provar que você existe
          </a>
        </div>

      </div>
    </div>
  );
};

/* ---- Sub-componentes ---- */

const bodyText = {
  fontSize: '1.05rem',
  lineHeight: 1.85,
  color: '#374151',
  marginBottom: '1rem',
};

const Section = ({ children }) => (
  <div style={{ marginBottom: '1.5rem' }}>{children}</div>
);

const SectionTitle = ({ children }) => (
  <h2 style={{
    fontSize: '1.5rem',
    fontWeight: '400',
    color: '#1e1b4b',
    marginBottom: '1.25rem',
    letterSpacing: '-0.01em',
    paddingBottom: '0.5rem',
    borderBottom: '1px solid #ede9fe',
    fontFamily: "'Georgia', serif"
  }}>
    {children}
  </h2>
);

const Divider = () => (
  <div style={{ borderTop: '1px solid #ede9fe', margin: '3rem 0' }} />
);

const Callout = ({ text }) => (
  <p style={{
    fontStyle: 'italic',
    color: '#6d28d9',
    fontSize: '1.1rem',
    lineHeight: 1.7,
    borderLeft: '3px solid #c4b5fd',
    paddingLeft: '1.25rem',
    margin: '1.5rem 0',
  }}>
    {text}
  </p>
);

const ServiceCard = ({ color, bg, title, text, highlight }) => (
  <div style={{ background: bg, borderLeft: `4px solid ${color}`, borderRadius: '0 10px 10px 0', padding: '1.5rem 1.75rem' }}>
    <h3 style={{ fontSize: '1.15rem', fontWeight: '700', color, marginBottom: '0.75rem', fontFamily: 'sans-serif' }}>{title}</h3>
    <p style={{ ...bodyText, marginBottom: highlight ? '0.75rem' : 0 }}>{text}</p>
    {highlight && <p style={{ fontWeight: '600', color, fontSize: '0.95rem', margin: 0 }}>{highlight}</p>}
  </div>
);

const ShopLink = ({ href, label, sublabel, icon }) => (
  <a href={href} target="_blank" rel="noopener noreferrer"
    style={{ display: 'inline-flex', alignItems: 'center', gap: '0.75rem', background: '#fff', border: '1px solid #bbf7d0', borderRadius: '8px', padding: '0.85rem 1.25rem', textDecoration: 'none', minWidth: '220px' }}>
    <span style={{ fontSize: '1.5rem' }}>{icon}</span>
    <span style={{ display: 'flex', flexDirection: 'column' }}>
      <span style={{ color: '#16a34a', fontWeight: '700', fontFamily: 'sans-serif', fontSize: '0.9rem' }}>{label}</span>
      <span style={{ color: '#9ca3af', fontSize: '0.78rem', marginTop: '0.1rem' }}>{sublabel}</span>
    </span>
  </a>
);

const SocialLink = ({ href, label, sublabel }) => (
  <a href={href} target="_blank" rel="noopener noreferrer"
    style={{ display: 'inline-flex', flexDirection: 'column', background: '#fff', border: '1px solid #e9d5ff', borderRadius: '8px', padding: '0.75rem 1.25rem', textDecoration: 'none' }}>
    <span style={{ color: '#7c3aed', fontWeight: '700', fontFamily: 'sans-serif', fontSize: '0.95rem' }}>{label}</span>
    <span style={{ color: '#9ca3af', fontSize: '0.8rem', marginTop: '0.15rem' }}>{sublabel}</span>
  </a>
);

export default About;
